"use client";

import Image from "next/image";
import type { ReactNode } from "react";
import { useI18n } from "@/i18n";
import { formatCurrencyFromCents } from "@/config/currency";
import { birthDateInputToIso, maskBirthDateInput } from "@/lib/birthDate";
import { CheckoutAddonCard } from "./CheckoutAddonCard";
import { CheckIcon, FileIcon, HeartIcon } from "./checkoutIcons";
import type {
  LoveMapCreate,
  LoveMapDesiredOutcome,
  LoveMapRelationshipSituation,
} from "@/types/tarot";

export const LOVE_MAP_SITUATIONS: readonly LoveMapRelationshipSituation[] = [
  "getting_to_know",
  "apart",
  "separated",
  "dating",
  "married",
];

export const LOVE_MAP_OUTCOMES: readonly LoveMapDesiredOutcome[] = [
  "reconnect",
  "reconcile",
  "commitment",
  "strengthen",
  "clarity",
];

const LOVE_MAP_INCLUDES = ["panorama", "quarters", "guidance"] as const;

// Product artwork: "photo" (language-neutral, under review with Bruna) or
// "pages" (real pages of the delivered PDF, rendered per language).
const LOVE_MAP_ARTWORK: "photo" | "pages" = "photo";
const LOVE_MAP_IMAGE_LANGUAGES: ReadonlySet<string> = new Set(["pt-BR", "pt-PT", "en-US"]);

/** Renders `**text**` segments of a translated string in bold. */
function emphasize(text: string): ReactNode[] {
  return text.split(/\*\*(.+?)\*\*/g).map((part, index) =>
    index % 2 === 1 ? (
      <strong key={index} className="font-semibold text-[#4a3230]">
        {part}
      </strong>
    ) : (
      part
    ),
  );
}

export interface LoveMapDraft {
  relationship_situation: LoveMapRelationshipSituation | "";
  desired_outcome: LoveMapDesiredOutcome | "";
  birth_date_input: string;
}

export type LoveMapField = keyof LoveMapDraft;

export const EMPTY_LOVE_MAP_DRAFT: LoveMapDraft = {
  relationship_situation: "",
  desired_outcome: "",
  birth_date_input: "",
};

/** Validate the draft; returns the API payload and the fields still missing. */
export function resolveLoveMapDraft(
  draft: LoveMapDraft,
  dayFirstDate: boolean,
): { payload: LoveMapCreate | null; missing: Set<LoveMapField> } {
  const missing = new Set<LoveMapField>();
  if (!draft.relationship_situation) missing.add("relationship_situation");
  if (!draft.desired_outcome) missing.add("desired_outcome");
  const birthDate = birthDateInputToIso(draft.birth_date_input, dayFirstDate);
  if (!birthDate) missing.add("birth_date_input");

  if (missing.size > 0 || !draft.relationship_situation || !draft.desired_outcome) {
    return { payload: null, missing };
  }
  return {
    payload: {
      relationship_situation: draft.relationship_situation,
      desired_outcome: draft.desired_outcome,
      channeled_person_birth_date: birthDate,
    },
    missing,
  };
}

interface LoveMapUpsellProps {
  readonly feeCents: number;
  readonly forecastYear: number;
  readonly deliveryTime: string;
  readonly channeledName: string;
  readonly selected: boolean;
  readonly onSelectedChange: (selected: boolean) => void;
  readonly draft: LoveMapDraft;
  readonly onDraftChange: (draft: LoveMapDraft) => void;
  readonly errors: ReadonlySet<LoveMapField>;
  readonly disabled?: boolean;
}

export function LoveMapUpsell({
  feeCents,
  forecastYear,
  deliveryTime,
  channeledName,
  selected,
  onSelectedChange,
  draft,
  onDraftChange,
  errors,
  disabled = false,
}: LoveMapUpsellProps) {
  const { t, lang } = useI18n();
  const year = String(forecastYear);
  const dayFirstDate = lang !== "en-US";
  const birthDateComplete = draft.birth_date_input.replace(/\D/g, "").length === 8;
  const birthDateInvalid =
    birthDateComplete && !birthDateInputToIso(draft.birth_date_input, dayFirstDate);

  const fieldClass = (field: LoveMapField, invalid = false) =>
    `form-input-compact w-full focus:outline-none transition-all duration-300 ${
      errors.has(field) || invalid
        ? "border-red-500 focus:border-red-600"
        : "focus:border-[rgba(232,115,115,0.5)]"
    }`;

  const requiredError = (field: LoveMapField) =>
    errors.has(field) && (
      <p className="mt-1 text-xs font-medium text-red-600">
        ⚠️{" "}
        {field === "birth_date_input"
          ? t("common.error.required", "Campo obrigatório")
          : t("common.error.select_option", "Selecione uma opção")}
      </p>
    );

  return (
    <CheckoutAddonCard
      tone="rose"
      checked={selected}
      onChange={onSelectedChange}
      disabled={disabled}
      icon={<HeartIcon className="h-4 w-4 text-[#e07a86] sm:h-5 sm:w-5" />}
      title={t("step8.love_map.name", "Mapa do Amor {year}").replace("{year}", year)}
      optionalLabel={t("step8.addon.optional", "Opcional")}
      price={formatCurrencyFromCents(feeCents)}
      after={
        selected && (
        <div className="mt-4 space-y-3 border-t border-[#f0d4d4] pt-4">
          <p className="text-sm text-[#4a3230]">
            {t(
              "step8.love_map.form_intro",
              "Para personalizar sua previsão, conte um pouco mais:",
            )}
          </p>

          <div>
            <label htmlFor="love-map-situation" className="form-label-compact">
              {t("step8.love_map.situation.label", "Situação atual da relação *")}
            </label>
            <select
              id="love-map-situation"
              value={draft.relationship_situation}
              disabled={disabled}
              onChange={(event) =>
                onDraftChange({
                  ...draft,
                  relationship_situation: event.target.value as LoveMapRelationshipSituation,
                })
              }
              className={fieldClass("relationship_situation")}
            >
              <option value="">
                {t("step8.love_map.select_placeholder", "Selecione uma opção")}
              </option>
              {LOVE_MAP_SITUATIONS.map((situation) => (
                <option key={situation} value={situation}>
                  {t(`step8.love_map.situation.${situation}`, situation)}
                </option>
              ))}
            </select>
            {requiredError("relationship_situation")}
          </div>

          <div>
            <label htmlFor="love-map-outcome" className="form-label-compact">
              {t(
                "step8.love_map.outcome.label",
                "O que você deseja viver com essa pessoa em {year}? *",
              ).replace("{year}", year)}
            </label>
            <select
              id="love-map-outcome"
              value={draft.desired_outcome}
              disabled={disabled}
              onChange={(event) =>
                onDraftChange({
                  ...draft,
                  desired_outcome: event.target.value as LoveMapDesiredOutcome,
                })
              }
              className={fieldClass("desired_outcome")}
            >
              <option value="">
                {t("step8.love_map.select_placeholder", "Selecione uma opção")}
              </option>
              {LOVE_MAP_OUTCOMES.map((outcome) => (
                <option key={outcome} value={outcome}>
                  {t(`step8.love_map.outcome.${outcome}`, outcome)}
                </option>
              ))}
            </select>
            {requiredError("desired_outcome")}
          </div>

          <div>
            <label htmlFor="love-map-birth-date" className="form-label-compact">
              {t(
                "step8.love_map.birth_date.label",
                "Data de nascimento de {name} *",
              ).replace("{name}", channeledName)}
            </label>
            <input
              id="love-map-birth-date"
              type="text"
              inputMode="numeric"
              maxLength={10}
              value={draft.birth_date_input}
              disabled={disabled}
              placeholder={dayFirstDate ? "DD/MM/AAAA" : "MM/DD/YYYY"}
              onChange={(event) =>
                onDraftChange({
                  ...draft,
                  birth_date_input: maskBirthDateInput(event.target.value),
                })
              }
              className={fieldClass("birth_date_input", birthDateInvalid)}
            />
            {birthDateInvalid ? (
              <p className="mt-1 text-xs font-medium text-red-600">
                ⚠️{" "}
                {t(
                  "step2.birth_date.invalid",
                  "Data inválida. Verifique o dia, mês e ano.",
                )}
              </p>
            ) : (
              requiredError("birth_date_input")
            )}
          </div>
        </div>
        )
      }
    >
      <span className="mt-1 block font-title text-[15px] leading-snug text-[#b4505c] sm:text-lg">
        {t("step8.love_map.title", "Existe um futuro pra nós em {year}?").replace("{year}", year)}
      </span>
      <span className="mt-1 block text-[13px] leading-snug text-[#4a3230] sm:text-sm sm:leading-relaxed">
        {emphasize(
          t(
            "step8.love_map.description",
            "Uma **previsão astrológica personalizada** para vocês dois, feita a partir dos signos de vocês e dos movimentos dos planetas em {year}.",
          ).replaceAll("{year}", year),
        )}
      </span>

      <span className="mt-3 flex flex-col gap-3 sm:mt-4 sm:flex-row sm:items-center sm:gap-4">
        <span className="block flex-1 space-y-2 sm:space-y-3">
          {LOVE_MAP_INCLUDES.map((item) => (
            <span key={item} className="flex items-start gap-2.5">
              <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#f8dcdc] text-[#b4505c]">
                <CheckIcon />
              </span>
              <span className="block">
                <span className="block text-sm font-semibold text-[#5c1616]">
                  {t(`step8.love_map.include.${item}.title`, item).replaceAll("{year}", year)}
                </span>
                <span className="block text-xs leading-snug text-[#8a6a5f] sm:text-[13px]">
                  {t(`step8.love_map.include.${item}.desc`, "").replaceAll("{year}", year)}
                </span>
              </span>
            </span>
          ))}
        </span>
        {LOVE_MAP_ARTWORK === "photo" ? (
          <Image
            src="/images/mapa-do-amor-foto.webp"
            alt={t(
              "step8.love_map.photo_alt",
              "Casal olhando o pôr do sol no mar, com o ano {year} no céu",
            ).replace("{year}", year)}
            width={600}
            height={600}
            className="mx-auto h-auto w-full max-w-[150px] shrink-0 rounded-xl shadow-md sm:mx-0 sm:w-40 sm:max-w-none"
          />
        ) : (
          <Image
            src={`/images/mapa-do-amor-${LOVE_MAP_IMAGE_LANGUAGES.has(lang) ? lang : "pt-BR"}.webp`}
            alt={t(
              "step8.love_map.image_alt",
              "Páginas do Mapa do Amor {year}: capa, panorama do casal e previsão por trimestre",
            ).replace("{year}", year)}
            width={465}
            height={367}
            className="mx-auto h-auto w-full max-w-[170px] shrink-0 sm:mx-0 sm:w-52 sm:max-w-none"
          />
        )}
      </span>

      <span className="mt-3 flex items-center gap-2 border-t border-[#f0d4d4] pt-2.5 text-[11px] italic leading-snug text-[#8a6a5f] sm:mt-4 sm:pt-3 sm:text-xs">
        <FileIcon className="h-4 w-4 shrink-0 not-italic text-[#b4505c]" />
        <span>
          {emphasize(
            t(
              "step8.love_map.delivery",
              "**PDF ilustrado**, entregue separadamente da sua carta em até {time} após a confirmação do pagamento.",
            ).replace("{time}", deliveryTime),
          )}
        </span>
      </span>
    </CheckoutAddonCard>
  );
}
